import { useMemo } from 'react';

import { useGetWizardTypesQuery } from '../api/wizardsApi';
import type { StepConfig, WizardConfig } from '../types/wizard';

/**
 * useWizardConfig Hook
 *
 * Loads wizard configuration and builds step list based on:
 * - Wizard type configuration
 * - skipEnvStep flags
 * - Provider-specific steps (e.g., SFTP file processing)
 * - Dynamic scenario injection
 *
 * Based on wizardFormLoader.js makeSteps() and componentWillMount()
 */
export const useWizardConfig = (
  wizardID: string | undefined,
  appId: string,
  existingConfig?: WizardConfig
) => {
  const { data: wizardTypes, isLoading } = useGetWizardTypesQuery(undefined, {
    skip: !!existingConfig, // Skip if we already have config
  });

  const config = useMemo(() => {
    if (existingConfig) return existingConfig;
    if (!wizardTypes || !wizardID) return null;

    return wizardTypes.find((w: any) => String(w.typeId) === String(wizardID));
  }, [wizardTypes, wizardID, existingConfig]);

  const steps = useMemo(() => {
    if (!config) return [];

    const stepList: StepConfig[] = [];

    // Step 1: Name (always included)
    stepList.push({
      key: 'name',
      type: 'name',
      label: 'Name',
      name: config.title,
    });

    // Step 2: Environments (combined source + destination)
    // Skip if both skipEnvStep flags are true
    if (!config.source.skipEnvStep || !config.destination.skipEnvStep) {
      stepList.push({
        key: 'env',
        type: 'env',
        label: 'Environments',
        sourceConfig: config.source,
        destinationConfig: config.destination,
      });
    }

    // Step 3: File Processing (only for SFTP source)
    if (config.source.type === 'SFTP') {
      stepList.push({
        key: 'file',
        type: 'file',
        label: 'File Processing',
        mode: 'source',
      });
    }

    // Step 4: Scenario (dynamically injected - placeholder for now)
    // Will be inserted after scenarios are fetched
    // Note: Actual injection happens in useEffect in WizardFormPage

    // Step 5: Schema Selection (always included for source)
    stepList.push({
      key: 'schema',
      type: 'schema',
      label: 'Object Mapping',
      mode: 'source',
      sourceConfig: config.source,
      destinationConfig: config.destination,
    });

    // Step 6: Field Mapping (always included)
    stepList.push({
      key: 'map',
      type: 'map',
      label: 'Field Mapping',
      customizable: config.destination.customizable,
      typeGuid: config.typeGuid,
    });

    // Step 7: Deploy (always included)
    stepList.push({
      key: 'deploy',
      type: 'deploy',
      label: 'Deploy',
      options: ['once', 'daily', 'repeating'],
    });

    return stepList;
  }, [config]);

  /**
   * Dynamic scenario injection based on available scenarios
   * Called when scenario list is loaded from WizardFormPage
   * Returns updated steps array if injection should happen
   *
   * Based on wizardFormLoader.js componentDidUpdate scenario injection logic
   */
  const injectScenarioStep = (
    scenarios: any[],
    currentSteps: StepConfig[]
  ): StepConfig[] | null => {
    // Only inject if:
    // 1. Scenarios exist
    // 2. Scenario step not already present
    // 3. Config supports scenarios
    if (
      scenarios.length === 0 ||
      currentSteps.find((s) => s.type === 'scenario') ||
      !config?.typeGuid
    ) {
      return null;
    }

    // Clone steps for modification
    const steps = [...currentSteps];
    let injectIndex = -1;

    // Find injection point (after 'env' or 'file' step)
    steps.forEach((step, i) => {
      if (step.type === 'env') injectIndex = i;
      if (step.type === 'file') injectIndex = i;
    });

    if (injectIndex !== -1) {
      steps.splice(injectIndex + 1, 0, {
        key: 'scenario',
        type: 'scenario',
        label: 'Data Scenario',
        magicNumber: '2147483647',
        options: scenarios,
      });
      return steps;
    }

    return null;
  };

  /**
   * Check if scenario step should be conditionally shown
   * based on wizard configuration and tenant settings
   */
  const shouldShowScenarioStep = (): boolean => {
    // Show scenario step if typeGuid matches certain patterns
    // Typically for specific integrations that require scenario selection
    return !!config?.typeGuid && config.typeGuid.includes('_');
  };

  return {
    config,
    steps,
    isLoading,
    error: null,
    injectScenarioStep,
    shouldShowScenarioStep,
  };
};
